import React, { useRef } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { SERVICES } from '../constants';
import { ServiceItem } from '../types';
import { useTilt, Spotlight } from './interactions';
import { Magnetic } from './Magnetic';
import { ServiceVisual } from './ServiceVisuals';
import { Link } from './nav';

const THEMES = [
  { card: 'bg-zinc-100 text-black', muted: 'text-zinc-600', chip: 'bg-white text-black', line: 'border-zinc-300', button: 'bg-black text-white' },
  { card: 'bg-[#0A0A0A] text-white', muted: 'text-white/60', chip: 'bg-white/10 text-white', line: 'border-white/15', button: 'bg-white text-black' },
  { card: 'bg-[#703FEC] text-white', muted: 'text-white/75', chip: 'bg-white/15 text-white', line: 'border-white/25', button: 'bg-white text-black' },
  { card: 'bg-white text-black border border-zinc-200', muted: 'text-zinc-600', chip: 'bg-zinc-100 text-black', line: 'border-zinc-200', button: 'bg-black text-white' },
];

const ServiceCard: React.FC<{
  service: ServiceItem;
  index: number;
  total: number;
  progress: MotionValue<number>;
  showStart?: boolean;
}> = ({ service, index, total, progress, showStart }) => {
  const theme = THEMES[index % THEMES.length];
  // ...and dim a little, so the active card reads as "on top"
  const dim = useTransform(progress, [Math.min(1, (index + 0.85) / total), Math.min(1, (index + 1.3) / total)], [0, index === total - 1 ? 0 : 0.3]);
  const { px, py, handlers } = useTilt(0);

  const spot = index === 1 ? 'rgba(112,63,236,0.35)' : index === 2 ? 'rgba(255,255,255,0.18)' : 'rgba(112,63,236,0.14)';

  return (
    <div className="sticky h-auto" style={{ top: `calc(6rem + ${index * 1.5}rem)` }}>
      <motion.article
        {...handlers}
        className={`group/card relative overflow-hidden origin-top rounded-[2rem] md:rounded-[3rem] p-6 md:p-12 lg:p-14 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-14 lg:min-h-[68vh] shadow-[0_-20px_60px_rgba(0,0,0,0.06)] ${theme.card}`}
      >
        <Spotlight x={px} y={py} color={spot} size={600} />
        <motion.div aria-hidden style={{ opacity: dim }} className="pointer-events-none absolute inset-0 z-30 bg-black" />
        <span aria-hidden className={`pointer-events-none absolute -right-4 -bottom-10 md:-bottom-16 z-0 font-bold leading-none tracking-tighter text-[10rem] md:text-[18rem] opacity-[0.06] transition-transform duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/card:-translate-y-4`}>{service.number}</span>
        <div className="relative z-10 flex flex-col justify-between gap-8">
          <div>
            <div className={`flex items-center justify-between border-b pb-5 mb-8 md:mb-12 ${theme.line}`}>
              <span className="font-mono text-sm">{service.number} / {String(total).padStart(2, '0')}</span>
              <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${theme.muted}`}>Capability</span>
            </div>
            <h3 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.9] mb-6 md:mb-8">
              {/* Letters ripple on hover; each word stays whole when the title wraps */}
              {service.title.split(' ').map((word, w, words) => {
                const offset = words.slice(0, w).join(' ').length + (w ? 1 : 0);
                return (
                  <React.Fragment key={w}>
                    <span className="inline-block whitespace-nowrap">
                      {word.split('').map((ch, i) => (
                        <span
                          key={i}
                          className="inline-block transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/card:-translate-y-1.5"
                          style={{ transitionDelay: `${(offset + i) * 18}ms` }}
                        >
                          {ch}
                        </span>
                      ))}
                    </span>
                    {w < words.length - 1 && ' '}
                  </React.Fragment>
                );
              })}
            </h3>
            <p className={`text-base md:text-xl leading-relaxed max-w-lg ${theme.muted}`}>{service.description}</p>
            {service.links && (
              <div className="mt-6 md:mt-8">
                <span className={`block mb-3 text-[10px] font-bold uppercase tracking-[0.2em] ${theme.muted}`}>Explore</span>
                <div className="flex flex-wrap gap-2">
                  {service.links.map((l) => {
                    const cls = `group/link inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#703FEC] hover:border-[#703FEC] hover:text-white ${theme.line}`;
                    return l.external ? (
                      <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className={cls}>
                        {l.label}
                        <span className="transition-transform duration-300 group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5">↗</span>
                      </a>
                    ) : (
                      <Link key={l.href} href={l.href} className={cls}>
                        {l.label}
                        <span className="transition-transform duration-300 group-hover/link:-rotate-45">→</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex flex-wrap gap-2 mb-6">
              {service.tags.map((tag) => (
                <span key={tag} className={`rounded-full px-4 py-2 text-xs font-semibold cursor-default transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F3350C] hover:text-white ${theme.chip}`}>{tag}</span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <p className={`text-xs ${theme.muted}`}>
                <span className="font-bold uppercase tracking-[0.2em] text-[10px] mr-2">Ideal for</span>
                {service.idealFor}
              </p>
              {showStart && (
                <Magnetic strength={0.35}>
                  <Link
                    href="/contact"
                    className={`group shrink-0 self-start inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-[12px] font-bold transition-transform hover:scale-[1.05] ${theme.button}`}
                  >
                    Start a project
                    <span className="w-8 h-8 rounded-full bg-[#703FEC] text-white flex items-center justify-center transition-transform duration-500 group-hover:-rotate-45">→</span>
                  </Link>
                </Magnetic>
              )}
            </div>
          </div>
        </div>

        {/* Revealed once as it arrives; nothing here is tied to scroll, so stacking stays smooth */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 overflow-hidden rounded-[1.5rem] md:rounded-[2rem] h-72 md:h-96 lg:h-auto lg:min-h-[440px] [contain:paint]"
        >
          <ServiceVisual index={index} />
        </motion.div>
      </motion.article>
    </div>
  );
};

export const Services: React.FC = () => {
  const stackRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: stackRef, offset: ['start start', 'end end'] });

  return (
    <section id="services" className="relative z-10 rounded-t-[2.5rem] md:rounded-t-[4rem] shadow-[0_-40px_100px_rgba(0,0,0,0.45)] pt-20 md:pt-28 pb-24 md:pb-40 px-2 md:px-3 bg-white text-black">
      <div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 md:mb-20 px-4 md:px-9">
          <div>
            <div className="flex gap-3 items-center mb-6">
              <div className="w-1.5 h-1.5 bg-[#F3350C] rounded-full" />
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black">Capabilities</p>
            </div>
            <h2 className="text-6xl md:text-8xl font-bold tracking-tight leading-[0.85]">
              Services<span className="text-[#703FEC]">.</span>
            </h2>
          </div>
          <div className="flex items-end gap-6 max-w-md">
            <p className="text-zinc-600 text-base leading-relaxed">
              Four ways we help startups and small businesses grow, wherever they are. Pick one, or combine them into a single project.
            </p>
            <span className="text-[#703FEC] font-mono text-lg shrink-0">(04)</span>
          </div>
          <div className="md:self-end">
            <Link href="/services" className="group inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-black hover:text-[#703FEC] transition-colors whitespace-nowrap">
              All services <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>

        <div ref={stackRef} className="relative flex flex-col gap-6 md:gap-10 pb-[10vh]">
          {SERVICES.map((service, i) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={i}
              total={SERVICES.length}
              progress={scrollYProgress}
              showStart
            />
          ))}
        </div>
      </div>
    </section>
  );
};
